import React, { useState } from "react";
import { motion } from "framer-motion";
import BookDemoForm from "../../../../components/BookDemoForm/BookDemoForm";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSitemap,
  faSearch,
  faTools,
  faMagic,
  faEnvelopeOpenText,
  faFileAlt,
  faChartLine,
  faComments,
  faTasks,
  faUserCog,
  faDatabase,
  faHeadset,
  faLink,
} from "@fortawesome/free-solid-svg-icons";

const NewHeroSection2 = () => {
  const [showFormModal, setShowFormModal] = useState(false);

  const handleFormToggle = () => {
    setShowFormModal(!showFormModal);
  };

  const steps = [
    {
      icon: faSitemap,
      step: "STEP 1",
      title: "We map out your processes",
      description:
        "We’ll create a visual map of all your systems, manual tasks, and apps.",
    },
    {
      icon: faSearch,
      step: "STEP 2",
      title: "We find areas to add AI and automate",
      description:
        "We audit your workflows to pinpoint opportunities with the highest ROI.",
    },
    {
      icon: faTools,
      step: "STEP 3",
      title: "We build and test",
      description:
        "We use a mix of custom code, AI tools, Zapier, Make.com, and your tech stack.",
    },
    {
      icon: faMagic,
      step: "STEP 4",
      title: "We manage and iterate",
      description:
        "Every client we work with grows, so there’s always new things to automate.",
    },
  ];

  const useCases = [
    {
      icon: faEnvelopeOpenText,
      title: "Sales and marketing automation",
      description:
        "Content creation, full-funnel automation, lead routing, CRM, outbound, payments, contracts, and much more.",
    },
    {
      icon: faFileAlt,
      title: "Image, voice, text recognition and analysis",
      description:
        "Analyze and parse documents, biometrics, emotion, facial, products, videos, and more.",
    },
    {
      icon: faChartLine,
      title: "Predictive analytics",
      description:
        "Data-driven decision making through risk analytics, demand forecasting, and future planning tasks.",
    },
    {
      icon: faComments,
      title: "Conversational AI",
      description:
        "Prompt engineering, automatic replies, customer service, sales chatbots, and front-office automation.",
    },
    {
      icon: faTasks,
      title: "Project management",
      description:
        "Automate processes like onboarding, admin tasks, accounting, and invoicing.",
    },
    {
      icon: faUserCog,
      title: "Staff augmentation",
      description:
        "Save employees hours per day by automating tedious tasks. We’ve even replaced entire teams of virtual assistants.",
    },
    {
      icon: faDatabase,
      title: "Data silos",
      description:
        "Automate data entry, collection, cleansing, and reporting for fast and accurate decision making.",
    },
    {
      icon: faHeadset,
      title: "Customer service",
      description:
        "Most customer service questions are repetitive. Automate CS tasks for faster resolution.",
    },
    {
      icon: faLink,
      title: "Connect apps",
      description:
        "Integrate your apps for seamless data handoff and consolidated information management.",
    },
  ];

  return (
    <div className="bg-gray-100 text-gray-900">
      <div className="container mx-auto px-6 py-16 max-w-7xl text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">
          How our <span className="text-blue-600">AI</span> &{" "}
          <span className="text-blue-600">Automation</span> Agency Works
        </h1>
        <p className="text-lg md:text-xl mb-12 max-w-3xl mx-auto">
          We analyze your business processes and replace manual, repetitive
          bottlenecks with AI-powered automation.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="flex flex-col items-center text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 h-full"
              whileHover={{ scale: 1.05 }}
            >
              <div className="w-20 h-20 flex items-center justify-center bg-blue-100 rounded-full shadow-md mb-4">
                <FontAwesomeIcon
                  icon={step.icon}
                  className="text-4xl text-blue-600"
                />
              </div>
              <h2 className="text-blue-600 font-semibold mb-2">{step.step}</h2>
              <h3 className="text-xl font-bold mb-2">{step.title}</h3>
              <p className="text-gray-700 text-sm md:text-base">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="bg-white text-gray-900 py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold mb-6">
            The Endless Use Cases of <span className="text-blue-600">AI</span> &{" "}
            <span className="text-blue-600">Automation</span>
          </h1>
          <p className="text-lg mb-12 max-w-3xl mx-auto">
            Our clients enhance efficiency by automating repetitive tasks,
            saving hours of manual work.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center bg-gray-50 p-6 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 h-full"
                whileHover={{ scale: 1.05 }}
              >
                <FontAwesomeIcon
                  icon={useCase.icon}
                  className="text-blue-600 text-5xl mb-4"
                />
                <h2 className="text-xl font-bold mb-2">{useCase.title}</h2>
                <p className="text-gray-700">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-black text-white flex flex-col md:flex-row items-center justify-between px-6 md:px-20 py-16">
      {/* Left Section */}
      <motion.div
        className="text-center md:text-left mb-8 md:mb-0"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">
          Let's explore
          <br />
          use cases for AI
        </h1>
      </motion.div>

      {/* Right Section */}
      <motion.div
        className="text-center md:text-left max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <p className="mb-6 text-lg md:text-xl text-gray-300">
          We turn manual, siloed, and inconsistent work into operational
          excellence with our AI and automation services.
        </p>

        <button
          className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-3 px-6 rounded-full shadow-lg transition-transform transform hover:scale-105"
          onClick={handleFormToggle}
        >
          Free Consultation
        </button>
      </motion.div>
    </div>
      {showFormModal && <BookDemoForm onClose={handleFormToggle} />}
    </div>
  );
};

export default NewHeroSection2;
